import { createRouter, createWebHistory } from 'vue-router'
import { authGuard, guestGuard } from '@/features/auth/guards/auth.guard'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { isRoleAllowedInCrm } from '@/features/auth/types/auth.types'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import { NO_ACCESS_PATH, sectionGuard } from './guards/section.guard'
import './route-meta'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'root',
      redirect: '/dashboard',
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/features/auth/views/LoginView.vue'),
      beforeEnter: guestGuard,
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/features/auth/views/ForgotPasswordView.vue'),
      beforeEnter: guestGuard,
      meta: {
        requiresAuth: false,
      },
    },
    {
      // URL из письма auth (DEV-704): {base}/reset-password/{token} — для не-агентских ролей
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('@/features/auth/views/ResetPasswordView.vue'),
      meta: {
        requiresAuth: false,
      },
    },
    {
      // URL из письма auth (DEV-1308): {base}/accept-invite/{token}. Сюда ведут
      // приглашения всех ролей, кроме тех, что открывают только кабинет агента —
      // те уходят на agents.{host}. Развилку считает бэкенд по apps ролей.
      //
      // Без guestGuard намеренно: письмо открывают и с уже залогиненной вкладки,
      // и выкидывать человека на дашборд вместо приглашения было бы тупиком.
      path: '/accept-invite/:token',
      name: 'accept-invite',
      component: () => import('@/features/auth/views/AcceptInviteView.vue'),
      meta: {
        requiresAuth: false,
      },
    },
    {
      // DEV-1253. Принципал не принадлежит приложению: карта разделов пуста.
      // Живёт ВНЕ дашборда — рисовать layout не из чего, сайдбар был бы пустым.
      path: '/no-access',
      name: 'no-access',
      component: () => import('@/features/auth/views/NoAccessView.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/dashboard',
      component: DashboardLayout,
      beforeEnter: authGuard,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/features/dashboard/views/DashboardView.vue'),
          meta: {
            section: 'dashboard',
            breadcrumbs: [{ label: 'i18n:common.navigation.home', to: '/dashboard' }],
          },
        },
        {
          // DEV-1253. Отказ в конкретном разделе — человек в приложение допущен,
          // поэтому экран внутри layout: сайдбар показывает, куда ему можно.
          path: 'forbidden',
          name: 'forbidden',
          component: () => import('@/features/auth/views/ForbiddenView.vue'),
          meta: {
            section: 'self',
          },
        },
        {
          path: 'clients',
          name: 'clients',
          component: () => import('@/features/clients/views/ClientsListView.vue'),
          meta: {
            section: 'clients',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.clients', to: '/dashboard/clients' },
            ],
          },
        },
        {
          path: 'clients/create',
          name: 'clients-create',
          component: () => import('@/features/clients/views/CreateClientWizard.vue'),
          meta: {
            section: 'clients',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.clients', to: '/dashboard/clients' },
              { label: 'i18n:clients.actions.createClient' },
            ],
          },
        },
        {
          path: 'clients/:id',
          name: 'clients-detail',
          component: () => import('@/features/clients/views/ClientDetailView.vue'),
          meta: {
            section: 'clients',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.clients', to: '/dashboard/clients' },
              { label: ':name', dynamic: true },
            ],
          },
        },
        {
          path: 'clients/:id/edit',
          name: 'clients-edit',
          component: () => import('@/features/clients/views/ClientFormView.vue'),
          meta: {
            section: 'clients',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.clients', to: '/dashboard/clients' },
              { label: ':name', dynamic: true },
              { label: 'i18n:common.actions.edit' },
            ],
          },
        },
        // DEV-476. CRM-раздел «Агенты» (реестр + карточка + создание).
        {
          path: 'agents',
          name: 'agents',
          component: () => import('@/features/agents/views/AgentsListView.vue'),
          meta: {
            section: 'agents',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.agents', to: '/dashboard/agents' },
            ],
          },
        },
        {
          path: 'agents/create',
          name: 'agents-create',
          component: () => import('@/features/agents/views/CreateAgentView.vue'),
          meta: {
            section: 'agents',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.agents', to: '/dashboard/agents' },
              { label: 'i18n:agentsRegistry.actions.addAgent' },
            ],
          },
        },
        {
          path: 'agents/:id',
          name: 'agents-detail',
          component: () => import('@/features/agents/views/AgentDetailView.vue'),
          meta: {
            section: 'agents',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.agents', to: '/dashboard/agents' },
              { label: ':name', dynamic: true },
            ],
          },
        },
        // DEV-478. Компании-партнёры (реестр + карточка + создание).
        {
          path: 'partner-companies',
          name: 'partner-companies',
          component: () => import('@/features/partner-companies/views/CompaniesListView.vue'),
          meta: {
            section: 'partner_companies',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:partnerCompanies.title', to: '/dashboard/partner-companies' },
            ],
          },
        },
        {
          path: 'partner-companies/create',
          name: 'partner-companies-create',
          component: () => import('@/features/partner-companies/views/CompanyCreateView.vue'),
          meta: {
            section: 'partner_companies',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:partnerCompanies.title', to: '/dashboard/partner-companies' },
              { label: 'i18n:partnerCompanies.actions.addCompany' },
            ],
          },
        },
        {
          path: 'partner-companies/:id',
          name: 'partner-companies-detail',
          component: () => import('@/features/partner-companies/views/CompanyDetailView.vue'),
          meta: {
            section: 'partner_companies',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:partnerCompanies.title', to: '/dashboard/partner-companies' },
              { label: ':name', dynamic: true },
            ],
          },
        },
        {
          path: 'calls',
          name: 'call-log',
          component: () => import('@/features/telephony/views/CallLogView.vue'),
          meta: {
            section: 'calls',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'Журнал звонков', to: '/dashboard/calls' },
            ],
          },
        },
        {
          path: 'deals',
          name: 'deals',
          component: () => import('@/features/deals/views/DealsListView.vue'),
          meta: {
            section: 'deals',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.deals', to: '/dashboard/deals' },
            ],
          },
        },
        {
          path: 'deals/create',
          name: 'deals-create',
          component: () => import('@/features/deals/views/DealFormView.vue'),
          meta: {
            section: 'deals',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.deals', to: '/dashboard/deals' },
              { label: 'i18n:deals.createDeal' },
            ],
          },
        },
        {
          path: 'deals/:id',
          name: 'deals-detail',
          component: () => import('@/features/deals/views/DealDetailView.vue'),
          meta: {
            section: 'deals',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.deals', to: '/dashboard/deals' },
              { label: ':title', dynamic: true },
            ],
          },
        },
        {
          path: 'properties',
          name: 'properties',
          component: () => import('@/features/properties/views/PropertiesListViewModern.vue'),
          meta: {
            section: 'properties',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList' },
            ],
          },
        },
        {
          // Сравнение открывается из карточки объекта, своего пункта меню и
          // своего раздела в реестре у него нет — отсюда 'self'.
          path: 'properties/compare',
          name: 'properties-compare',
          component: () => import('@/features/properties/views/PropertyCompareView.vue'),
          meta: {
            section: 'self',
          },
        },
        // DEV-1080. Конструктор проекта: create/edit в CRM (раньше — только админка).
        // 'properties/new' объявлен до 'properties/:id', чтобы слово new не
        // трактовалось как идентификатор проекта.
        {
          path: 'properties/new',
          name: 'properties-create',
          component: () => import('@/features/properties/views/ComplexFormView.vue'),
          meta: {
            section: 'properties',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              { label: 'i18n:properties.form.titleNew' },
            ],
          },
        },
        {
          path: 'properties/:id/edit',
          name: 'properties-edit',
          component: () => import('@/features/properties/views/ComplexFormView.vue'),
          meta: {
            section: 'properties',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              { label: ':slug', dynamic: true },
            ],
          },
        },
        // DEV-1160. Импорт проектов из папки Google Drive — способ завести проект
        // целиком по данным сборщика. Объявлен до 'properties/:id' по той же
        // причине, что и 'properties/new'.
        {
          path: 'properties/import-drive',
          name: 'properties-import-drive',
          component: () => import('@/features/properties/views/DriveImportView.vue'),
          meta: {
            section: 'properties_import',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              { label: 'i18n:properties.driveImport.title' },
            ],
          },
        },
        // DEV-1083. Импорт прайса файлом: визард и черновики. Drive-импорт их не
        // заменяет — он заводит проект из папки целиком, а прайс приходит
        // отдельным файлом и позже, в том числе по проектам, заведённым руками.
        // Раздел общий с Drive-импортом: оба заводят объекты и гейтятся
        // properties:create.
        {
          path: 'properties/import',
          name: 'properties-import',
          component: () => import('@/features/properties/views/ImportWizardView.vue'),
          meta: {
            section: 'properties_import',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              { label: 'i18n:properties.import.title' },
            ],
          },
        },
        {
          path: 'properties/import-drafts',
          name: 'properties-import-drafts',
          component: () => import('@/features/properties/views/ImportDraftsView.vue'),
          meta: {
            section: 'properties_import',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              { label: 'i18n:properties.import.drafts' },
            ],
          },
        },
        {
          path: 'properties/import/:id',
          name: 'properties-import-draft',
          component: () => import('@/features/properties/views/ImportDraftView.vue'),
          meta: {
            section: 'properties_import',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              {
                label: 'i18n:properties.import.drafts',
                to: '/dashboard/properties/import-drafts',
              },
              { label: 'i18n:properties.import.title' },
            ],
          },
        },
        // Блоки проекта отдельным экраном (макет PC-08): на список блоков можно
        // дать ссылку, а состояние переживает перезагрузку — в модалке ни того,
        // ни другого не было.
        {
          path: 'properties/:id/blocks',
          name: 'properties-blocks',
          component: () => import('@/features/properties/views/ComplexBlocksView.vue'),
          meta: {
            section: 'properties',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              { label: ':slug', dynamic: true },
            ],
          },
        },
        {
          path: 'properties/:id',
          name: 'properties-detail',
          component: () => import('@/features/properties/views/PropertyDetailViewModern.vue'),
          meta: {
            section: 'properties',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.projectsList', to: '/dashboard/properties' },
              { label: ':slug', dynamic: true },
            ],
          },
        },
        {
          path: 'notifications',
          name: 'notifications',
          component: () => import('@/features/notifications/views/NotificationsView.vue'),
          meta: {
            section: 'notifications',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:notifications.title' },
            ],
          },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/features/profile/views/ProfileView.vue'),
          meta: {
            section: 'profile',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:profile.title' },
            ],
          },
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/features/settings/views/SettingsView.vue'),
          meta: {
            section: 'settings',
          },
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import('@/features/reports/views/ReportsView.vue'),
          meta: {
            section: 'reports',
          },
        },
        {
          // DEV-801/802 (F2/F3). Аналитика ЖК — темп реализации и динамика цен (harvester).
          path: 'rc-analytics',
          name: 'rc-analytics',
          component: () => import('@/features/rc-analytics/views/RcAnalyticsListView.vue'),
          meta: {
            section: 'rc_analytics',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.rcAnalytics' },
            ],
          },
        },
        {
          // DEV-824. Модерация projectid-маппингов ЖК.
          // Статический путь ДО rc-analytics/:id, чтобы :id не перехватил 'moderation'.
          path: 'rc-analytics/moderation',
          name: 'rc-analytics-moderation',
          component: () => import('@/features/rc-analytics/views/RcModerationView.vue'),
          meta: {
            section: 'rc_moderation',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.rcAnalytics', to: '/dashboard/rc-analytics' },
              { label: 'i18n:common.navigation.rcModeration' },
            ],
          },
        },
        {
          // DEV-930. Экран данных источника по projectid — копирование в форму создания ЖК.
          // Статический путь ДО rc-analytics/:id (иначе :id перехватит 'moderation').
          path: 'rc-analytics/moderation/source-data/:id',
          name: 'rc-analytics-source-data',
          component: () => import('@/features/rc-analytics/views/RcSourceDataView.vue'),
          meta: {
            section: 'rc_moderation',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.rcAnalytics', to: '/dashboard/rc-analytics' },
              {
                label: 'i18n:common.navigation.rcModeration',
                to: '/dashboard/rc-analytics/moderation',
              },
              { label: 'i18n:common.navigation.rcSourceData' },
            ],
          },
        },
        {
          path: 'rc-analytics/:id',
          name: 'rc-analytics-detail',
          component: () => import('@/features/rc-analytics/views/RcAnalyticsDetailView.vue'),
          meta: {
            section: 'rc_analytics',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.rcAnalytics', to: '/dashboard/rc-analytics' },
            ],
          },
        },
        {
          // DEV-352. Маршрутизация обращений агентов.
          path: 'routing-rules',
          name: 'routing-rules',
          component: () => import('@/features/routing/views/RoutingRulesView.vue'),
          meta: {
            section: 'routing_rules',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.routingRules' },
            ],
          },
        },
        {
          // DEV-1267. Админ-раздел: пользователи. Право на сам раздел — users:read,
          // мутации гейтятся своими правами на бэкенде.
          path: 'admin/users',
          name: 'admin-users',
          component: () => import('@/features/admin/views/AdminUsersView.vue'),
          meta: {
            section: 'admin_users',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminUsers' },
            ],
          },
        },
        {
          // Карточка пользователя. Раздел тот же, поэтому и section тот же:
          // section.guard решает доступ по разделу, а не по конкретному экрану.
          path: 'admin/users/:id',
          name: 'admin-user-detail',
          component: () => import('@/features/admin/views/AdminUserDetailView.vue'),
          meta: {
            section: 'admin_users',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminUsers', to: '/dashboard/admin/users' },
              // Имя человека карточка проставляет сама через updateBreadcrumb:
              // в момент разбора маршрута оно ещё неизвестно.
              { label: ':name', dynamic: true },
            ],
          },
        },
        {
          // DEV-1267. Приглашения — отдельный раздел группы «Администрирование»
          // (макет 744:370), со своим ключом и своими крошками.
          path: 'admin/invitations',
          name: 'admin-invitations',
          component: () => import('@/features/admin/views/AdminInvitationsView.vue'),
          meta: {
            section: 'admin_invitations',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminInvitations' },
            ],
          },
        },
        {
          // DEV-1268. Роли и права. Раздел гейтится users:read — тем же правом,
          // что и ListRoles; мутации требуют роли ADMIN и отбиваются сервером.
          path: 'admin/roles',
          name: 'admin-roles',
          component: () => import('@/features/admin/views/AdminRolesView.vue'),
          meta: {
            section: 'admin_roles',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminRoles' },
            ],
          },
        },
        {
          // Матрица роль × право. Раздел тот же: section.guard решает доступ по
          // разделу, а не по экрану.
          path: 'admin/roles/matrix',
          name: 'admin-role-matrix',
          component: () => import('@/features/admin/views/AdminRoleMatrixView.vue'),
          meta: {
            section: 'admin_roles',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminRoles', to: '/dashboard/admin/roles' },
              { label: 'i18n:admin.rbac.matrix.title' },
            ],
          },
        },
        {
          // DEV-1268. Реестр разделов, только чтение. Гейтится sections:manage —
          // правом, которое есть только у ADMIN (миграция 65): users:read пустил
          // бы сюда HOS, а внутри его встретил бы PermissionDenied.
          path: 'admin/sections',
          name: 'admin-sections',
          component: () => import('@/features/admin/views/AdminSectionsView.vue'),
          meta: {
            section: 'admin_sections',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminSections' },
            ],
          },
        },
        {
          // DEV-1268. Журнал изменений доступов.
          path: 'admin/audit',
          name: 'admin-audit',
          component: () => import('@/features/admin/views/AdminAuditView.vue'),
          meta: {
            section: 'admin_audit',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminAudit' },
            ],
          },
        },
        {
          // DEV-1786 (эпик DEV-1781). Лендинги: раздел «Администрирования».
          // Право на раздел — landings:read; публикация и правка SEO гейтятся
          // landings:publish на бэкенде.
          path: 'admin/landings',
          name: 'admin-landings',
          component: () => import('@/features/landings/views/LandingsListView.vue'),
          meta: {
            section: 'admin_landings',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminLandings' },
            ],
          },
        },
        {
          // Карточка лендинга. Раздел тот же: гард решает доступ по разделу,
          // а не по конкретному экрану.
          path: 'admin/landings/:name',
          name: 'admin-landing-detail',
          component: () => import('@/features/landings/views/LandingDetailView.vue'),
          meta: {
            section: 'admin_landings',
            breadcrumbs: [
              { label: 'i18n:common.navigation.administration' },
              { label: 'i18n:common.navigation.adminLandings', to: '/dashboard/admin/landings' },
              { label: ':name', dynamic: true },
            ],
          },
        },
        {
          // DEV-522. Конструктор воронки канбана (pipeline:* на бэке).
          path: 'pipeline',
          name: 'pipeline-constructor',
          component: () => import('@/features/pipeline/views/PipelineConstructorView.vue'),
          meta: {
            section: 'pipeline',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.pipelineConstructor' },
            ],
          },
        },
        {
          // DEV-436. Веб-инбокс обращений агентов («свои vs все» решает бэк).
          path: 'agent-tickets',
          name: 'agent-tickets',
          component: () => import('@/features/agent-tickets/views/AgentTicketsView.vue'),
          meta: {
            section: 'agent_tickets',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.agentTickets' },
            ],
          },
        },
        {
          path: 'agent-tickets/:id',
          name: 'agent-tickets-detail',
          component: () => import('@/features/agent-tickets/views/AgentTicketDetailView.vue'),
          meta: {
            section: 'agent_tickets',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.agentTickets', to: '/dashboard/agent-tickets' },
              { label: 'i18n:agentTickets.detail.breadcrumb' },
            ],
          },
        },
        {
          // DEV-437. Управление агентами + назначение куратора.
          path: 'agents-admin',
          name: 'agents-admin',
          component: () => import('@/features/agents-admin/views/AgentsAdminView.vue'),
          meta: {
            section: 'agents_admin',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.agentsAdmin' },
            ],
          },
        },
        {
          // DEV-795. Очередь модерации/приёма компаний-партнёров.
          path: 'company-verifications',
          name: 'company-verifications',
          component: () =>
            import('@/features/company-verifications/views/CompanyVerificationsView.vue'),
          meta: {
            section: 'company_verifications',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.companyVerifications' },
            ],
          },
        },
        {
          path: 'documents',
          name: 'documents',
          component: () => import('@/features/documents/views/DocumentsView.vue'),
          meta: {
            section: 'documents',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.documents' },
            ],
          },
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('@/features/calendar/views/CalendarView.vue'),
          meta: {
            section: 'calendar',
            breadcrumbs: [
              { label: 'i18n:common.navigation.home', to: '/dashboard' },
              { label: 'i18n:common.navigation.calendar' },
            ],
          },
        },
      ],
    },
    // Legacy routes for backward compatibility
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
    },
  ],
})

// Global navigation guard - auth initialization + RBAC
let isInitialized = false

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Initialize auth ONCE on app start if tokens exist
  if (!isInitialized) {
    const hasTokens = localStorage.getItem('accessToken') && localStorage.getItem('refreshToken')

    if (hasTokens) {
      await authStore.initialize()
    }

    isInitialized = true
  }

  // DEV-1167/DEV-1177. Партнёр — внешний интегратор: заводится тем же auth,
  // логинится той же формой и до этой проверки проваливался прямо в /dashboard.
  //
  // DEV-1253. Ролевой allowlist намеренно остаётся до волны 5: его заменит
  // фильтр по roles.apps на сервере, и снимать защиту раньше замены нельзя.
  //
  // DEV-1255. Сессию при этом НЕ гасим. Раньше здесь был logout() с выбросом
  // на форму входа: человек залогинен корректно, а выглядело это как «вход
  // сломался», и он вводил тот же пароль по кругу. Ведём на /no-access, где
  // прямо сказано, под какой учётной записью он вошёл.
  if (
    authStore.isAuthenticated &&
    !isRoleAllowedInCrm(authStore.user?.roles) &&
    to.path !== NO_ACCESS_PATH
  ) {
    next(NO_ACCESS_PATH)
    return
  }

  // DEV-1253. Дальше решает карта разделов с сервера. Прежние правила —
  // MARKETER_BLOCKED_ROUTES, регексп формы проекта и whitelist менеджера
  // каталога — удалены: они описывали доступ третьим набором слов, который
  // расходился и с сайдбаром, и с тем, что отдаёт бэкенд.
  await sectionGuard(to, from, next)
})

export default router
